import { parseKnowledgeBaseCitation } from '../../../../../util/knowledgeBaseCitation';
import { isPractitionerType } from './Attachment';

/**
 * Ceiling on how many attachments one section resolves and renders.
 *
 * Every resolved attachment costs a slot in the FSS lookup plus, when it is
 * cited inline, a widget that is expensive on its own — a 600px document panel
 * or a biomarker chart that fetches its own history. Sections that accumulate
 * hundreds of citations drag the report to a crawl, so the set is capped and
 * the budget is spent on the resource types clinicians actually read.
 */
export const MAX_SECTION_ATTACHMENTS = 50;

const KNOWLEDGE_BASE = 'KnowledgeBase';

/**
 * Resource types in the order they earn a slot in the attachment budget.
 * Documents and biomarkers are the substance of a report; notes, medications,
 * conditions and survey responses are supporting context. Anything unlisted
 * sorts last.
 */
const ATTACHMENT_TYPE_PRIORITY: readonly string[] = [
  'DocumentReference',
  'Observation',
  KNOWLEDGE_BASE,
  'Communication',
  'MedicationRequest',
  'Condition',
  'QuestionnaireResponse',
  'CarePlan',
  'Patient',
];

/**
 * Citation-id prefixes are the fallback discriminator for references that
 * aren't shaped like `ResourceType/id`.
 */
const CITATION_ID_PREFIX_TO_TYPE: Record<string, string> = {
  dr: 'DocumentReference',
  mr: 'MedicationRequest',
  o: 'Observation',
  c: 'Condition',
};

const getReferenceResourceType = (
  attachment: fhir.Reference,
): string | undefined => {
  const reference = attachment.reference ?? '';

  if (parseKnowledgeBaseCitation(reference)) {
    return KNOWLEDGE_BASE;
  }

  const [maybeType, id] = reference.split('/');
  if (id && ATTACHMENT_TYPE_PRIORITY.includes(maybeType)) {
    return maybeType;
  }

  const prefix = /^(dr|mr|o|c)/.exec(attachment.identifier?.value ?? '')?.[1];
  return prefix ? CITATION_ID_PREFIX_TO_TYPE[prefix] : undefined;
};

const getPriority = (attachment: fhir.Reference): number => {
  const resourceType = getReferenceResourceType(attachment);
  const index = resourceType
    ? ATTACHMENT_TYPE_PRIORITY.indexOf(resourceType)
    : -1;
  return index === -1 ? ATTACHMENT_TYPE_PRIORITY.length : index;
};

/**
 * Picks the attachments worth resolving for a section — highest-value resource
 * types first, capped at `limit`, and returned in their original citation order
 * so the cap decides *which* attachments survive without reshuffling the ones
 * that do.
 *
 * Repeat citations of the same resource collapse into one entry so the budget
 * counts distinct records; the surfaces rendering these lists dedupe by
 * reference anyway. Practitioner references are dropped outright because no
 * surface renders them, and they would otherwise consume slots invisibly.
 */
export const prioritizeAttachments = (
  attachments: fhir.Reference[],
  limit: number = MAX_SECTION_ATTACHMENTS,
): fhir.Reference[] => {
  const seen = new Set<string>();
  const deduplicated = attachments.filter(attachment => {
    if (isPractitionerType(attachment)) return false;
    const key = attachment.reference ?? attachment.identifier?.value;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  if (deduplicated.length <= limit) {
    return deduplicated;
  }

  return deduplicated
    .map((attachment, index) => ({
      attachment,
      index,
      priority: getPriority(attachment),
    }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .slice(0, limit)
    .sort((a, b) => a.index - b.index)
    .map(({ attachment }) => attachment);
};
